import { useSelector } from 'react-redux';

import { RootState } from '../../../store';
import { BadgeStatusIcon } from '../../shared/icons/icons';
import IconTextGroup from '../../shared/iconTextGroup/IconTextGroup';
import { ArchiveProjectStatus } from '../../../definitions/enums/archiveEnums';
import {
    ProjectStatus,
    InvitationStatus,
    TimelineState,
    RubricStatus,
} from '../../../utils';
import { ProjectCreationStatus } from '../../../components/shared/ProjectProgressBar/projectCreationProgressBarStatus';
import { TopicStatus } from '../../../components/projectTopic/ProjectTopicSidebar';

import './StatusBadge.css';

interface StatusBadgeProps {
    status: string;
    classes?: string;
    marginBottom?: boolean;
    statusLabel?: string;
    removeDefaultStyling?: boolean;
}

export const getStatusBadgeCssClass = (status: string): string => {
    switch (status) {
        case ProjectStatus.approved: {
            return 'status-badge-icon-approved';
        }
        case InvitationStatus.accepted: {
            return 'status-badge-icon-accepted';
        }
        case ArchiveProjectStatus.COMPLETED: {
            return 'status-badge-icon-completed';
        }
        case ArchiveProjectStatus.OVERDUE_APPROVED:
        case ArchiveProjectStatus.OVERDUE_PENDING:
        case ArchiveProjectStatus.OVERDUE_DECLINED:
        case ArchiveProjectStatus.OVERDUE_REGISTERED:
        case ArchiveProjectStatus.OVERDUE_HANDED_IN: {
            return 'status-badge-icon-overdue';
        }
        case ProjectStatus.pending: {
            return 'status-badge-icon-pending';
        }
        case ProjectStatus.registered: {
            return 'status-badge-icon-registered';
        }
        case ProjectStatus.handedIn: {
            return 'status-badge-icon-handed-in';
        }
        case ProjectStatus.declined: {
            return 'status-badge-icon-declined';
        }
        case ArchiveProjectStatus.WITHDRAWN: {
            return 'status-badge-icon-withdrawn';
        }
        case ArchiveProjectStatus.USER_DELETED: {
            return 'status-badge-icon-deleted';
        }
        case ProjectStatus.failed:
        case ArchiveProjectStatus.FAILED: {
            return 'status-badge-icon-failed';
        }
        case ProjectCreationStatus.Saved: {
            return 'status-badge-icon-saved';
        }
        case ProjectStatus.evaluated: {
            return 'status-badge-icon-evaluated';
        }
        case TopicStatus.Archived: {
            return 'status-badge-icon-archived';
        }
        case TimelineState.Future: {
            return 'status-badge-icon-future';
        }
        case TimelineState.Archived: {
            return 'status-badge-icon-archived';
        }
        case TimelineState.Ongoing:
        case RubricStatus.Ongoing: {
            return 'status-badge-icon-ongoing';
        }
        case RubricStatus.NotInUse: {
            return 'status-badge-icon-not-in-use';
        }
        case RubricStatus.Published: {
            return 'status-badge-icon-published';
        }
        case RubricStatus.Draft: {
            return 'status-badge-icon-draft';
        }
        default: {
            return 'status-badge-icon-other';
        }
    }
};
export const getStatusBadgeIcon = (status: string): JSX.Element => {
    const iconClassName = getStatusBadgeCssClass(status);
    return BadgeStatusIcon(iconClassName);
};

const getLocalizedStatus = (status: string) => {
    const localization = useSelector((state: RootState) => state.localization);
    return (
        localization.localizationStrings.susy.project.projectStatus[status] ??
        status
    );
};

export const StatusBadge = ({
    status,
    marginBottom,
    classes,
    statusLabel,
    removeDefaultStyling,
}: StatusBadgeProps): JSX.Element => {
    const getClassesForWrapper = () => {
        const classesForWrapper = ['status-badge', 'bg-transparent'];
        if (classes) {
            classesForWrapper.push(classes);
        }
        if (marginBottom) {
            classesForWrapper.push('mb-0');
        }
        if (!removeDefaultStyling) {
            classesForWrapper.push('default-styling');
        }

        return classesForWrapper.join(' ');
    };

    return (
        <IconTextGroup
            classes={getClassesForWrapper()}
            avatarsOrIcons={[getStatusBadgeIcon(status)]}
            text={getLocalizedStatus(statusLabel ?? status)}
            dataTestId={`status-badge-icon-text-group-${status}`}
            isClickable={false}
        />
    );
};
